import window from "core/includes/window";
import { getCountryCodeAndCurrencyCode } from "core/includes/countries";

interface GA4Params {
    currency: string;
    item_list_id?: string;
    item_list_name?: string;
    value?: number;
    transaction_id?: string;
    coupon?: string;
    shipping?: number;
    tax?: number;
    payment_type?: string;
    shipping_tier?: string;
    items?: GA4Item[];
}

interface GA4Item {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price?: number;
    quantity?: number;
}

/**
 * @param eventName
 * @param params
 */
export const trackEvent = (eventName: string, params: GA4Params) => {
    if (typeof window !== "undefined" && (window as any).gtag) {
        const eventData = {
            ...params,
        };
        (window as any).gtag("event", eventName, eventData);
    }
};

/**
 * For products, the list-view and view GA4 functions use the same item parameters.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item_item
 *
 * @param products
 */
const mapToGA4ItemForProduct = (products: ProductItem[]): GA4Item[] => (
    products.map((product: ProductItem, index: number) => ({
        index,
        item_id: product.id,
        item_name: product.title,
        item_brand: "Writer's toolbox",
        item_category: product.groupName, // Group name
        item_category2: product.suitabilityName, // Suitability name
        price: Number(product.price.amount || 0),
        discount: Number(product.price.amount || 0) - Number(product.price.discountedPrice || 0),
        quantity: Number(product.inventoryQuantity || 0), // Inventory quantity
    }))
);

/**
 * For cart items, the view, add, and remove GA4 functions use the same item parameters.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_cart_item
 *
 * @param cartProducts
 * @param requiredQuantity
 */
const mapToGA4ItemForCartProduct = (cartProducts: CartProduct[], requiredQuantity?: number): GA4Item[] => (
    cartProducts.map((cartProduct: CartProduct, index: number) => {
        const quantity = requiredQuantity ?? Number(cartProduct.quantity || 0);
        return {
            index,
            item_id: String(cartProduct.id),
            item_name: cartProduct.title,
            item_brand: "Writer's toolbox",
            price: Number(cartProduct.unitPrice || 0),
            discount: Number(cartProduct.discountedPrice || 0) / quantity,
            quantity,
        };
    })
);

/**
 * @description Google Analytics 4 Events
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag
 */
export const analytics = {
    viewItemList: (
        items: ProductItem[],
        currency: string,
        listName: string = "All products",
        listId: string = "search_results"
    ) =>
        trackEvent(
            "view_item_list",
            {
                item_list_id: listId,
                item_list_name: listName,
                currency,
                items: mapToGA4ItemForProduct(items),
            },
        ),
    viewItem: (item: ProductItem, currency: string, value?: number) =>
        trackEvent(
            "view_item",
            {
                currency,
                value: value ? value : item.price.amount,
                items: mapToGA4ItemForProduct([item]),
            },
        ),
    /**
     * When it calls "remove all" on the Shopping Cart page
     * @param isAddToCart
     * @param prevItem
     * @param item
     * @param currency
     */
    updateCart: (
        isAddToCart: boolean,
        prevItem: CartProduct|null,
        item: CartProduct|null,
        currency: string,
    ) => {
        const targetItem = isAddToCart ? item : prevItem;
        const { value, requiredQuantity } = getPriceDetailsForCartItems(isAddToCart, prevItem, item);

        trackEvent(
            isAddToCart ? "add_to_cart" : "remove_from_cart",
            {
                currency,
                value,
                items: mapToGA4ItemForCartProduct(targetItem ? [targetItem] : [], requiredQuantity),
            },
        )
    },
    clearCart: (cart: Cart) => {
        const value = cart.products.reduce((accumulator, currentItem) => {
            return accumulator + currentItem.totalPrice;
        }, 0);

        trackEvent(
            "remove_from_cart",
            {
                currency: getCurrencyCode(cart.countryCode),
                value,
                items: mapToGA4ItemForCartProduct(cart.products),
            },
        )
    },
    viewCart: (cart: Cart) =>
        trackEvent(
            "view_cart",
            {
                currency: getCurrencyCode(cart.countryCode),
                value: cart.subtotalAmount,
                items: mapToGA4ItemForCartProduct(cart.products),
            },
        ),
    beginCheckout: (cart: Cart) =>
        trackEvent(
            "begin_checkout",
            {
                currency: getCurrencyCode(cart.countryCode),
                value: cart.subtotalAmount,
                items: mapToGA4ItemForCartProduct(cart.products),
            },
        ),
    addShippingInfo: (cart: Cart) =>
        trackEvent(
            "add_shipping_info",
            {
                currency: getCurrencyCode(cart.countryCode),
                value: cart.subtotalAmount,
                coupon: cart.discounts, // coupon code list (json string)
                items: mapToGA4ItemForCartProduct(cart.products),
            },
        ),
    addPaymentInfo: (isInvoice: boolean, cart: Cart) =>
        trackEvent(
            "add_payment_info",
            {
                currency: getCurrencyCode(cart.countryCode),
                value: cart.subtotalAmount,
                coupon: cart.discounts, // coupon code list (json string)
                payment_type: isInvoice ? "Invoice" : "Card",
                items: mapToGA4ItemForCartProduct(cart.products),
            },
        ),
    purchase: (transactionId: string, cart: Cart) =>
        trackEvent(
            "purchase",
            {
                transaction_id: transactionId,
                currency: getCurrencyCode(cart.countryCode),
                value: cart.subtotalAmount,
                tax: cart.taxAmount,
                shipping: cart.shippingAmount,
                items: mapToGA4ItemForCartProduct(cart.products),
            },
        ),
};

/**
 * Return the filtered result on the ProductList page.
 *
 * @param group
 * @param minPrice
 * @param maxPrice
 * @param suitability
 * @param sortField
 * @param sortDirection
 */
export const buildProductListFilters = (
    group?: string,
    minPrice?: number,
    maxPrice?: number,
    suitability?: string,
    sortField?: string,
    sortDirection?: string,
) => {
    const filters: string[] = [];

    if (group) {
        filters.push(`Category: ${group}`);
    }

    if (minPrice && maxPrice) {
        filters.push(`Price: ${minPrice}-${maxPrice}`);
    }

    if (suitability) {
        filters.push(`Suitability: ${suitability}`);
    }

    if (sortField && sortDirection) {
        filters.push(`Sort by: ${sortField} ${sortDirection}`);
    }

    const hasFilters = filters.length > 0;

    return {
        listName: hasFilters ? filters.join(", ") : "All products",
        listId: hasFilters ? "search_filtered_results" : "search_results",
    };
};

/**
 * Return only the incremental price and quantity of the item being added at this moment, not the total accumulated
 * amount or quantity in the cart.
 *
 * @param isAddToCart true: add, false: decrease or remove
 * @param prevItem The total state of the cart BEFORE the item is added or removed.
 * @param item The total state of the cart AFTER the item is added or removed.
 */
const getPriceDetailsForCartItems = (
    isAddToCart: boolean,
    prevItem: CartProduct|null,
    item?: CartProduct|null
) => {
    // Current total price
    const value = isAddToCart
        ? ((item?.totalPrice || 0) - (prevItem?.totalPrice || 0))
        : ((prevItem?.totalPrice || 0) - (item?.totalPrice || 0));

    // Current item quantity
    const quantity = isAddToCart
        ? ((item?.quantity || 0) - (prevItem?.quantity || 0))
        : ((prevItem?.quantity || 0) - (item?.quantity || 0));

    return {
        value: Math.max(0, value),
        requiredQuantity: Math.max(0, quantity),
    };
};

/**
 * Get currency code by the selected country code (e.g. NZD).
 *
 * @param countryCode
 */
const getCurrencyCode = (countryCode: string) => {
    const { currency } = getCountryCodeAndCurrencyCode(countryCode);
    return currency;
};
